import { apiClient } from "./apiClient";
import type { PreviewResponse } from "./distributionPreviewService";

export type OpeningInventoryStrategy = "conservative" | "balanced" | "broad";
export type OpeningInventoryStoreSize = "pequena" | "mediana" | "grande";
export type OpeningInventorySignal =
  | "sales"
  | "inventory"
  | "distribution"
  | "margin"
  | "opening";

export interface OpeningInventorySignalScore {
  sales: number;
  inventory: number;
  distribution: number;
  margin: number;
  opening: number;
}

export interface OpeningInventorySelectedLine {
  unified_product_id: string;
  product_code: string | null;
  product_name: string;
  qty: number;
  unit_cost: number;
  estimated_cost: number;
  origin_warehouse: string;
  score: number;
  dominant_signal: OpeningInventorySignal;
  signals: OpeningInventorySignalScore;
}

export interface OpeningInventoryContextResponse {
  store: string;
  display_name: string;
  store_size: OpeningInventoryStoreSize;
  store_size_source: "store_profile_manual" | "store_profile" | "default_pequena";
  capacity_units: number;
  capacity_source:
    | "override"
    | "store_capacity_manual"
    | "store_capacity"
    | "derived_by_size"
    | "derived_default";
  comparable_stores: string[];
  benchmark: {
    slp04_units: number;
    slp06_units: number;
    small_store_units_median: number;
  };
}

export interface OpeningInventorySummary {
  store: string;
  target_amount: number;
  strategy: OpeningInventoryStrategy;
  target_months: number;
  request_id: string;
  store_size: OpeningInventoryStoreSize;
  capacity_units: number;
  capacity_source: OpeningInventoryContextResponse["capacity_source"];
  comparable_stores: string[];
  candidate_skus: number;
  selected_skus: number;
  selected_units: number;
  estimated_cost: number;
  estimated_cost_iva: number;
  created_recommendations: number;
  updated_recommendations: number;
  skipped: {
    no_mapping: number;
    no_cost: number;
    no_origin: number;
    outside_budget: number;
    outside_capacity: number;
  };
  selected_lines: OpeningInventorySelectedLine[];
  references: Array<{
    key: "slp04" | "slp06";
    label: string;
    cutoff_date: string;
  }>;
}

export interface OpeningInventoryPreviewResponse {
  summary: OpeningInventorySummary;
  preview: PreviewResponse;
}

export const openingInventoryService = {
  async getContext(store: string): Promise<OpeningInventoryContextResponse> {
    const res = await apiClient.get<{
      ok: true;
      data: OpeningInventoryContextResponse;
    }>(`/distribution/opening-inventory/context/${store}`);
    return res.data.data;
  },

  async createPreview(input: {
    store: string;
    target_amount: number;
    strategy?: OpeningInventoryStrategy;
    capacity_override_units?: number;
    target_months?: number;
  }): Promise<OpeningInventoryPreviewResponse> {
    const res = await apiClient.post<{
      ok: true;
      data: OpeningInventoryPreviewResponse;
    }>(`/distribution/opening-inventory/preview`, input);
    return res.data.data;
  },
};
